import {
	Component,
	OnInit,
	OnDestroy,
	Input,
	Output,
	ViewChild,
	ElementRef,
	EventEmitter
} from '@angular/core';
import {
	UntypedFormBuilder,
	UntypedFormGroup,
	Validators
} from '@angular/forms';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CustomerService } from '../../services/customer.service';
import { PrintService } from '../../services/print.service'
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { InventoryService } from '../../services/inventory.service';
import { ReportsService } from '../../services/reports.service';

/** Upper bound for top-customers report (must match server `report::topCustomers` cap). */
export const TOP_CUSTOMERS_REPORT_MAX_LIMIT = 5000;

/** Column headers aligned with `customer-total` XLS/PDF export (`buildHtmlForExport`). */
const TOP_CUSTOMERS_EXPORT_HEADERS = [
	'Customer#',
	'Name',
	'Contact',
	'Phone',
	'Email',
	'Ship-To',
	'Bill-To',
	'Last Seen',
	'Sales',
	'Units',
	'Orders',
	'Avg Order',
] as const;

@Component({
	selector: 'app-top-customers',
	templateUrl: './top-customers.component.html',
	styleUrls: ['./top-customers.component.scss'],
})
export class TopCustomersComponent implements OnInit, OnDestroy {
	@Input() flip = 'inactive';
	@Input() limit = 100;
	@Output() viewData = new EventEmitter < any > ();

	readonly topCustomersMaxLimit = TOP_CUSTOMERS_REPORT_MAX_LIMIT;

	@ViewChild('customerDetailsRef') customerDetailsRef: ElementRef;

	title = 'Top ' + this.limit + ' Customers';
	viewing: any = false;
	data: any = false;
	topcustomers: any[] = [];
	/** When multiple salespeople are selected, one top-N list per rep (separate API runs). */
	perRepResults: Array<{ salesmancode: string; salesmanname: string; customers: any[] }> = [];
	customerdetails: any[] = [];
	categories: any[] = [];
	productlines: any[] = [];
	brands: any[] = [];
	salespeople: any[] = [];
	locations: any[] = [];
	issalesman = false;
	loading = true;
	showFilters = false;
	/** Same period picker as the Sales Team card. */
	readonly presets: any[] = [
		{ label: 'MTD',    mtd: true },
		{ label: '30d',    days: 30 },
		{ label: '60d',    days: 60 },
		{ label: '90d',    days: 90 },
		{ label: 'YTD',    ytd: true },
		{ label: 'Custom', custom: true },
	];
	activePreset = '';
	showPresets = false;
	customFrom = '';
	customTo = '';
	color = 'blue';
	searchForm: any = new UntypedFormGroup({});
	fromdate: Date;
	todate: Date;
	private subs: Subscription[] = [];

	private static readonly THEME_PALETTE: Record<string, { light: [number, number, number]; base: [number, number, number]; dark: [number, number, number] }> = {
		purple:  { light: [206, 147, 216], base: [156,  39, 176], dark: [ 74,  20, 140] },
		primary: { light: [206, 147, 216], base: [156,  39, 176], dark: [ 74,  20, 140] },
		blue:    { light: [144, 202, 249], base: [ 33, 150, 243], dark: [ 25, 118, 210] },
		info:    { light: [128, 222, 234], base: [  0, 188, 212], dark: [  0,  96, 100] },
		green:   { light: [165, 214, 167], base: [ 76, 175,  80], dark: [ 27,  94,  32] },
		success: { light: [165, 214, 167], base: [ 76, 175,  80], dark: [ 27,  94,  32] },
		orange:  { light: [255, 204, 128], base: [255, 152,   0], dark: [230,  81,   0] },
		warning: { light: [255, 224, 130], base: [255, 193,   7], dark: [255, 143,   0] },
		red:     { light: [239, 154, 154], base: [244,  67,  54], dark: [183,  28,  28] },
		danger:  { light: [239, 154, 154], base: [244,  67,  54], dark: [183,  28,  28] },
		rose:    { light: [244, 143, 177], base: [233,  30,  99], dark: [136,  14,  79] },
	};

	get palette() {
		return TopCustomersComponent.THEME_PALETTE[this.color] || TopCustomersComponent.THEME_PALETTE['blue'];
	}

	get themeVars(): { [key: string]: string } {
		const p = this.palette;
		const rgb  = (c: number[]) => `rgb(${c[0]},${c[1]},${c[2]})`;
		const rgba = (c: number[], a: number) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
		return {
			'--tc-accent':        rgb(p.base),
			'--tc-accent-dark':   rgb(p.dark),
			'--tc-accent-light':  rgb(p.light),
			'--tc-accent-tint':   rgba(p.base, 0.07),
			'--tc-accent-tint-2': rgba(p.base, 0.14),
			'--tc-accent-border': rgba(p.base, 0.20),
			'--tc-accent-bg':     rgba(p.light, 0.16),
		};
	}

	constructor(
		private customerService: CustomerService,
		private fb: UntypedFormBuilder,
		private modalService: NgbModal,
		private globalSearchService: GlobalSearchService,
		private printService: PrintService,
		private inventoryService: InventoryService,
		private reportsService: ReportsService
	) {
		this.color = 'rose';
	}

	ngOnInit(): void {
		this.title = 'Top ' + this.limit + ' Customers';
		const today = new Date();
		this.fromdate = new Date(today.getFullYear(), today.getMonth()-3, 1);
		this.todate = today;
		const lim = Math.min(TOP_CUSTOMERS_REPORT_MAX_LIMIT, Math.max(1, Math.floor(Number(this.limit)) || 100));
		this.limit = lim;
		// Gate to the location the user is signed in at (navbar-selected warehouse, else their default/POS location).
		const currentLoc = this.globalSearchService.resolveBrowseLoccode();
		const defaultLocArr = currentLoc ? [currentLoc] : [];
		this.searchForm = this.fb.group({
			fromDateCtrl: [this.fromdate],
			toDateCtrl: [this.todate],
			brand: [[]],
			category: [[]],
			prodline: [[]],
			salesperson: [[]],
			loccode: [defaultLocArr],
			limit: [lim, [Validators.min(1), Validators.max(TOP_CUSTOMERS_REPORT_MAX_LIMIT)]],
		});
		this.subs.push(this.globalSearchService.user.subscribe((u: any) => {
			this.issalesman = !!(u?.user?.issalesman);
		}));
		this.subs.push(this.globalSearchService.locations.subscribe((locs: any) => {
			this.locations = (locs as any[]) || [];
			// Once the location list arrives, default the filter to the signed-in location if it isn't already set.
			if (this.searchForm && this.locations.length) {
				const current = this.searchForm.get('loccode')?.value;
				const currentArr = Array.isArray(current) ? current : (current ? [current] : []);
				if (currentArr.length === 0) {
					const loc = this.globalSearchService.resolveBrowseLoccode();
					if (loc && this.locations.some((l: any) => l.loccode === loc)) {
						this.searchForm.get('loccode').setValue([loc], { emitEvent: false });
					}
				}
			}
		}));
		this.subs.push(this.inventoryService.getItemCategories().subscribe((c: any) => { this.categories = (c as any[]) || []; }));
		this.subs.push(this.inventoryService.getItemProductlines().subscribe((p: any) => { this.productlines = (p as any[]) || []; }));
		this.subs.push(this.inventoryService.getBrands().subscribe((b: any) => { this.brands = b || []; }));
		this.subs.push(this.reportsService.getSalesPeople().subscribe((sp: any) => { this.salespeople = sp || []; }));
		this.reloadCustomers();
	}

	ngOnDestroy(): void {
		this.subs.forEach((s) => { if (s) { s.unsubscribe(); } });
		this.subs = [];
	}

	branchAddress(row: any): string {
		if (!row) {
			return '';
		}
		const parts = [row.braddress1, row.braddress2, row.braddress3, row.braddress4, row.braddress5, row.braddress6]
			.map((p: string) => (p != null && String(p).trim() !== '' ? String(p).trim() : null))
			.filter(Boolean);
		return parts.join(', ');
	}

	billAddress(row: any): string {
		if (!row) {
			return '';
		}
		const parts = [row.bill_address1, row.bill_address2, row.bill_address3, row.bill_address4, row.bill_address5, row.bill_address6]
			.map((p: string) => (p != null && String(p).trim() !== '' ? String(p).trim() : null))
			.filter(Boolean);
		return parts.join(', ');
	}

	get reportLimit(): number {
		const v = Number(this.searchForm?.get('limit')?.value);
		if (!Number.isFinite(v)) {
			return 100;
		}
		return Math.min(TOP_CUSTOMERS_REPORT_MAX_LIMIT, Math.max(1, Math.floor(v)));
	}

	/** Multiple sales reps selected — each gets their own top-N (not one combined list). */
	get splitBySalesrep(): boolean {
		const v = this.searchForm?.value;
		return !this.issalesman && Array.isArray(v?.salesperson) && v.salesperson.length > 1;
	}

	/** Count shown on the dashboard card (sum of rows when split by rep). */
	get cardResultCount(): number {
		if (this.splitBySalesrep && this.perRepResults?.length) {
			return this.perRepResults.reduce((n, p) => n + (p.customers?.length || 0), 0);
		}
		return this.topcustomers?.length || 0;
	}

	get perRepResultsHasAnyRows(): boolean {
		return !!(this.perRepResults && this.perRepResults.some((p) => (p.customers?.length || 0) > 0));
	}

	private get allRows(): any[] {
		if (this.splitBySalesrep && this.perRepResults?.length) {
			return this.perRepResults.reduce((acc: any[], p) => acc.concat(p.customers || []), []);
		}
		return this.topcustomers || [];
	}

	get globalTotalSales(): number {
		return this.allRows.reduce((s, d) => s + (d.salestotal || 0), 0);
	}

	get globalTotalDeliveries(): number {
		return this.allRows.reduce((s, d) => s + (d.deliverycount || 0), 0);
	}

	get globalAvgDelivery(): number {
		const del = this.globalTotalDeliveries;
		return del > 0 ? this.globalTotalSales / del : 0;
	}

	get globalTotalUnits(): number {
		return this.allRows.reduce((s, d) => s + (d.qtytotal || 0), 0);
	}

	salespersonDisplayName(code: string): string {
		const sp = this.salespeople?.find((p: any) => p.salesmancode === code);
		return sp ? sp.salesmanname : code;
	}

	selectPreset(p: any): void {
		const preset = this.presets.find((x) => x.label === p?.label) || p;
		this.activePreset = preset.label;
		if (preset.custom) {
			// Seed the pickers from the window currently applied and leave the
			// row open — applyCustom() runs the query once both dates are set.
			if (!this.customFrom) { this.customFrom = this.isoDate(this.searchForm.value.fromDateCtrl); }
			if (!this.customTo)   { this.customTo   = this.isoDate(this.searchForm.value.toDateCtrl); }
			return;
		}
		this.showPresets = false;
		const today = new Date();
		let from: Date;
		if (preset.ytd) {
			from = new Date(today.getFullYear(), 0, 1);
		} else if (preset.days) {
			from = new Date(today.getTime() - preset.days * 864e5);
		} else {
			from = new Date(today.getFullYear(), today.getMonth(), 1); // MTD
		}
		this.searchForm.patchValue({ fromDateCtrl: from, toDateCtrl: today }, { emitEvent: false });
		this.reloadCustomers();
	}

	applyCustom(): void {
		if (!this.customFrom || !this.customTo) { return; }
		// ISO strings compare correctly; silently un-reverse a backwards range.
		if (this.customFrom > this.customTo) {
			const t = this.customFrom;
			this.customFrom = this.customTo;
			this.customTo = t;
		}
		this.activePreset = 'Custom';
		this.showPresets = false;
		this.searchForm.patchValue({
			fromDateCtrl: this.dateFromIso(this.customFrom),
			toDateCtrl: this.dateFromIso(this.customTo),
		}, { emitEvent: false });
		this.reloadCustomers();
	}

	applyFilters(): void {
		this.reloadCustomers();
	}

	clearFilters(): void {
		const loc = this.globalSearchService.resolveBrowseLoccode();
		this.searchForm.patchValue({
			brand: [],
			category: [],
			prodline: [],
			salesperson: [],
			loccode: loc ? [loc] : [],
		}, { emitEvent: false });
		this.reloadCustomers();
	}

	/** Location is excluded: it defaults to the signed-in warehouse, which is not a narrowing the user chose. */
	get filterCount(): number {
		const v = this.searchForm?.value || {};
		return (v.brand?.length || 0)
			+ (v.category?.length || 0)
			+ (v.prodline?.length || 0)
			+ (v.salesperson?.length || 0);
	}

	private isoDate(d: any): string {
		if (!(d instanceof Date) || isNaN(d.getTime())) { return ''; }
		const p = (n: number) => ('0' + n).slice(-2);
		return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
	}

	/** Local-midnight Date from 'YYYY-MM-DD' — new Date(iso) would parse as UTC and land a day early. */
	private dateFromIso(iso: string): Date {
		const parts = iso.split('-').map((n) => Number(n));
		return new Date(parts[0], parts[1] - 1, parts[2]);
	}

	reloadCustomers() {
		this.loading = true;
		const v = this.searchForm.value;
		const base: any = {
			limit: this.reportLimit,
			datefrom: v.fromDateCtrl,
			dateto: v.toDateCtrl,
		};
		if (v.brand?.length) {
			base.brand = v.brand;
		}
		if (v.category?.length) {
			base.category = v.category;
		}
		if (v.prodline?.length) {
			base.prodline = v.prodline;
		}
		if (v.loccode?.length) {
			base.loccode = Array.isArray(v.loccode) ? v.loccode : [v.loccode];
		}

		const spList = !this.issalesman && Array.isArray(v.salesperson)
			? v.salesperson.filter((c: string) => c !== '' && c != null)
			: [];

		if (!this.issalesman && spList.length > 1) {
			this.perRepResults = [];
			forkJoin(
				spList.map((code: string) =>
					this.customerService
						.getTopCustomers({ ...base, salesperson: [code] })
						.pipe(catchError(() => of([])))
				)
			).subscribe(
				(batches: any[][]) => {
					this.perRepResults = spList.map((code: string, i: number) => ({
						salesmancode: code,
						salesmanname: this.salespersonDisplayName(code),
						customers: Array.isArray(batches[i]) ? batches[i] : [],
					}));
					this.topcustomers = [];
					this.data = this.perRepResults;
					this.loading = false;
					if (this.flip === 'active') {
						this.viewing = [];
						this.viewData.emit({ data: this.perRepResults, title: this.title });
					}
				},
				() => {
					this.loading = false;
				}
			);
			return;
		}

		this.perRepResults = [];
		const data: any = { ...base };
		if (!this.issalesman && spList.length === 1) {
			data.salesperson = spList;
		}
		this.customerService.getTopCustomers(data).subscribe(
			(results: any[]) => {
				this.topcustomers = results || [];
				this.data = this.topcustomers;
				this.loading = false;
				this.viewing = this.data;
			},
			() => {
				this.loading = false;
			}
		);
	}

	getReportSubtitle() {
		const v = this.searchForm?.value;
		if (this.issalesman || !v?.salesperson?.length || !this.salespeople?.length) {
			return '';
		}
		const names = v.salesperson.map((c: string) => this.salespersonDisplayName(c));
		return 'Sales People: ' + names.join(', ');
	}

	toggleFlip(data: any, title: string) {
		this.flip = data ? (this.flip === 'active' ? 'inactive' : 'active') : 'inactive';

		if (this.flip === 'active') {
			this.viewing = this.splitBySalesrep ? [] : data;
			this.title = title;
			this.viewData.emit({ data: this.splitBySalesrep ? this.perRepResults : data, title: this.title });
		} else {
			this.viewData.emit(false);
		}
	}

	exportPdf() {
		const now = new Date();
		const html = this.generateTopCustomersHtml(this.viewing);
		const lim = this.reportLimit;
		const filename = `top${lim}_customers_${now.getMonth() + 1}${now.getDate()}${now.getFullYear()}`;

		const data = {
			content: this.globalSearchService.base64encode(html),
			filename,
			title: `Top ${lim} Customers`,
			subtitle: this.buildPrintSubtitle(now),
		};

		this.printService.pdf(data).subscribe((result: any) => {
			this.globalSearchService.downloadPdf(result.content, filename);
		});
	}

	exportXls() {
		const lim = this.reportLimit;
		const now = new Date();
		const filename = `top${lim}_customers_${now.getMonth() + 1}${now.getDate()}${now.getFullYear()}`;

		if (this.splitBySalesrep && this.perRepResults?.length) {
			const wb = XLSX.utils.book_new();
			const usedNames = new Set<string>();
			for (const seg of this.perRepResults) {
				const ws = this.buildTopCustomersWorksheet(seg.customers || [], {
					title: `Top ${lim} Customers`,
					subtitle: this.buildPrintSubtitleForRep(seg, now),
				});
				const sheetName = this.uniqueExcelSheetName(seg.salesmanname, seg.salesmancode, usedNames);
				XLSX.utils.book_append_sheet(wb, ws, sheetName);
			}
			XLSX.writeFile(wb, `${filename}.xlsx`);
			return;
		}

		if (!this.viewing?.length) {
			return;
		}
		const encoded = this.globalSearchService.base64encode(this.buildHtmlForXlsExport(this.viewing));
		const data = {
			content: encoded,
			filename,
			title: `Top ${lim} Customers`,
			subtitle: this.buildPrintSubtitle(now),
		};
		this.printService.xls(data).subscribe((result: any) => {
			this.globalSearchService.downloadXls(result.content, filename);
		});
	}

	/** Same subtitle pattern as {@link CustomerTotalComponent#exportXls} (criteria + ` created ` + timestamp). */
	private buildPrintSubtitle(now: Date): string {
		const lim = this.reportLimit;
		const parts: string[] = [];
		const sp = this.getReportSubtitle();
		if (sp) {
			parts.push(sp);
		}
		const loc = this.getSelectedLocationsLine();
		if (loc) {
			parts.push(loc);
		}
		parts.push(this.getExportDateRangeLine());
		parts.push(`Limit ${lim}`);
		return parts.join(' · ') + ' created ' + now.toLocaleString();
	}

	/** Human-readable list of the locations the report is filtered to (empty = all locations). */
	private getSelectedLocationsLine(): string {
		const v = this.searchForm?.value;
		const sel = Array.isArray(v?.loccode) ? v.loccode : (v?.loccode ? [v.loccode] : []);
		if (!sel.length) {
			return '';
		}
		const names = sel.map((code: string) => {
			const l = this.locations?.find((x: any) => x.loccode === code);
			return l ? (l.locationname || l.loccode) : code;
		});
		return 'Locations: ' + names.join(', ');
	}

	private buildPrintSubtitleForRep(
		seg: { salesmancode: string; salesmanname: string },
		now: Date
	): string {
		const lim = this.reportLimit;
		const parts = [
			`Salesperson: ${seg.salesmanname} (${seg.salesmancode})`,
		];
		const loc = this.getSelectedLocationsLine();
		if (loc) {
			parts.push(loc);
		}
		parts.push(this.getExportDateRangeLine());
		parts.push(`Limit ${lim}`);
		return parts.join(' · ') + ' created ' + now.toLocaleString();
	}

	/** HTML table for print/xls — matches `CustomerTotalComponent#buildHtmlForExport` structure. */
	private buildHtmlForXlsExport(rows: any[]): string {
		let html = '<table class="table table-fixed table-sm">';
		html += '<thead><tr>';
		TOP_CUSTOMERS_EXPORT_HEADERS.forEach((h) => {
			html += `<th>${h}</th>`;
		});
		html += '</tr></thead><tbody>';
		(rows || []).forEach((d: any) => {
			const ship = this.branchAddress(d);
			const bill = this.billAddress(d);
			html += '<tr>';
			html += `<td>${this.escHtml(`${d.debtorno}.${d.branchcode}`)}</td>`;
			html += `<td>${this.escHtml(d.name || d.deliverto || '')}</td>`;
			html += `<td>${this.escHtml(d.contactname || '')}</td>`;
			html += `<td>${this.escHtml(d.phoneno || '')}</td>`;
			html += `<td>${this.escHtml(d.email || '')}</td>`;
			html += `<td>${this.escHtml(ship)}</td>`;
			html += `<td>${this.escHtml(bill)}</td>`;
			html += `<td>${this.escHtml(d.lastseen || '')}</td>`;
			html += `<td>${(d.salestotal ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>`;
			html += `<td>${Number(d.qtytotal ?? 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>`;
			html += `<td>${Number(d.deliverycount ?? 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>`;
			html += `<td>${(d.avgdelivery ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>`;
			html += '</tr>';
		});
		html += '</tbody></table>';
		return html;
	}

	private getExportDateRangeLine(): string {
		const v = this.searchForm?.value;
		const df = v?.fromDateCtrl as Date | null | undefined;
		const dt = v?.toDateCtrl as Date | null | undefined;
		const opts: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
		if (df instanceof Date && !Number.isNaN(df.getTime()) && dt instanceof Date && !Number.isNaN(dt.getTime())) {
			return `${df.toLocaleDateString(undefined, opts)} – ${dt.toLocaleDateString(undefined, opts)}`;
		}
		return 'Date range (see filters)';
	}

	private escHtml(s: unknown): string {
		return String(s ?? '')
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	}

	private exportRowFromCustomer(d: any): (string | number)[] {
		return [
			`${d.debtorno}.${d.branchcode}`,
			d.name || d.deliverto || '',
			d.contactname || '',
			d.phoneno || '',
			d.email || '',
			this.branchAddress(d),
			this.billAddress(d),
			d.lastseen || '',
			Number(d.salestotal ?? 0),
			Number(d.qtytotal ?? 0),
			Number(d.deliverycount ?? 0),
			Number(d.avgdelivery ?? 0),
		];
	}

	private exportColumnWidths() {
		return [
			{ wch: 14 },
			{ wch: 30 },
			{ wch: 22 },
			{ wch: 14 },
			{ wch: 32 },
			{ wch: 38 },
			{ wch: 38 },
			{ wch: 14 },
			{ wch: 14 },
			{ wch: 10 },
			{ wch: 12 },
			{ wch: 14 },
		];
	}

	/** Client multi-sheet XLSX: merged title + subtitle (same semantics as `print/xls`), then data table. */
	private buildTopCustomersWorksheet(
		customers: any[],
		meta: { title: string; subtitle: string }
	): XLSX.WorkSheet {
		const headers = [...TOP_CUSTOMERS_EXPORT_HEADERS];
		const ncol = headers.length;
		const dataRows = (customers || []).map((d) => this.exportRowFromCustomer(d));
		const aoa: (string | number)[][] = [
			[meta.title],
			[meta.subtitle],
			[],
			headers,
			...dataRows,
		];
		const ws = XLSX.utils.aoa_to_sheet(aoa);
		ws['!merges'] = [
			{ s: { r: 0, c: 0 }, e: { r: 0, c: ncol - 1 } },
			{ s: { r: 1, c: 0 }, e: { r: 1, c: ncol - 1 } },
		];
		ws['!cols'] = this.exportColumnWidths();
		const headerRow0 = 3;
		const endRow0 = customers.length ? headerRow0 + customers.length : headerRow0;
		ws['!autofilter'] = {
			ref: XLSX.utils.encode_range({
				s: { r: headerRow0, c: 0 },
				e: { r: endRow0, c: ncol - 1 },
			}),
		};
		const moneyFmt = '"$"#,##0.00;[Red]\\-"$"#,##0.00';
		const intFmt = '#,##0';
		const numericCols: Array<{ c: number; fmt: string }> = [
			{ c: ncol - 4, fmt: moneyFmt },
			{ c: ncol - 3, fmt: intFmt },
			{ c: ncol - 2, fmt: intFmt },
			{ c: ncol - 1, fmt: moneyFmt },
		];
		for (let i = 0; i < customers.length; i++) {
			const r = headerRow0 + 1 + i;
			for (const col of numericCols) {
				const addr = XLSX.utils.encode_cell({ r, c: col.c });
				const cell = ws[addr];
				if (cell && cell.t === 'n') {
					cell.z = col.fmt;
				}
			}
		}
		return ws;
	}

	private uniqueExcelSheetName(displayName: string, code: string, used: Set<string>): string {
		const base = this.safeExcelSheetTitle(displayName, code);
		let name = base;
		let i = 1;
		while (used.has(name.toLowerCase())) {
			const suffix = '_' + i++;
			name = (base.slice(0, Math.max(1, 31 - suffix.length)) + suffix) as string;
		}
		used.add(name.toLowerCase());
		return name;
	}

	/** Excel worksheet name: max 31 chars; no \\ / * ? : [ ] */
	private safeExcelSheetTitle(displayName: string, code: string): string {
		const raw = (displayName && String(displayName).trim()) || String(code || 'Rep').trim() || 'Rep';
		const cleaned = raw.replace(/[:'\\/*?\[\]]/g, '_').replace(/\s+/g, ' ').trim();
		return cleaned.slice(0, 31) || 'Rep';
	}

	/** Shown above the grid; same text as export subtitle date line. */
	getReportDateRangeCaption(): string {
		return this.getExportDateRangeLine();
	}

	generateTopCustomersHtml(viewing: any[]): string {
		if (this.splitBySalesrep && this.perRepResults?.length) {
			let out = '';
			for (const seg of this.perRepResults) {
				const cap = this.escHtml(seg.salesmanname);
				out += `<div class="tc-print-section" style="margin:18px 0 10px;font-family:Arial,Helvetica,sans-serif;">
					<div style="font-size:15px;font-weight:600;color:#263238;">${cap}</div>
					<div style="font-size:11px;color:#78909c;margin-top:2px;">${this.escHtml(seg.salesmancode)}</div>
				</div>`;
				out += this.generateTopCustomersTableHtml(seg.customers);
			}
			return out || '<p><strong>No records to display.</strong></p>';
		}
		return this.generateTopCustomersTableHtml(viewing);
	}

	private generateTopCustomersTableHtml(viewing: any[]): string {
		if (!viewing?.length) {
			return '<p><strong>No records to display.</strong></p>';
		}
		return this.buildHtmlForXlsExport(viewing);
	}


	viewCustomer(debtor: any) {
		const req = {
			customerno: debtor.debtorno,
			branchcode: debtor.branchcode,
			details: true
		};

		this.customerService.getCustomer(debtor.debtorno, debtor.branchcode).subscribe(results => {
			this.customerdetails = Array.isArray(results) ? results : [results];
			this.modalService
				.open(this.customerDetailsRef, {
					ariaLabelledBy: 'modal-title',
					size: 'lg'
				})
				.result.finally(() => {
					this.customerdetails = [];
				});
		});
	}
}